/**
 * Portal invoice modal (View / Pay Now).
 *
 * Moved VERBATIM out of `pages/invoices.tsx` (Plan 2 chunk 2 task 2) so the add-ons page can
 * open the same modal for an add-on invoice. Behaviour is unchanged: the summary comes from
 * `GET /api/inv/:token` and payment goes through the authenticated portal route.
 *
 * ⛔ ONE COPY. The whole point of the move is that /invoices and /client/addons cannot drift
 * apart on how an invoice is shown or paid — if this is ever forked, a client will see two
 * different balances for the same document depending on which page they opened.
 */
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { makeAuthenticatedRequest } from "@/lib/auth";
import { apiUrl, assetUrl, externalLinkClick, isAppBuild, openExternal } from "@/lib/net";
import { Loader2, CheckCircle, Download, CreditCard, Receipt } from "lucide-react";
import { isTestInvoice, TEST_BADGE_LABEL, TEST_INVOICE_NOTE, TEST_PAY_LABEL } from "@shared/catering/pilotDisplay";

export interface InvoiceModalTarget {
  token: string;
  invoiceId: string | number;
  weddingId: string | number | undefined;
  /** Pay Now: scroll the pay section into view once the summary has loaded. */
  focusPay: boolean;
}

type InvGateway = 'razorpay' | 'cashfree';

interface InvSummary {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  customerName: string;
  eventTitle: string;
  weddingDate: string | null;
  timeSlot: string | null;
  description: string;
  /** The rate the tax is at (0 nil-rated, 18 package/custom). Absent from an older server → 18. */
  gstRate?: number;
  taxable: number;
  cgst: number;
  sgst: number;
  total: number;
  amountPaid: number;
  balance: number;
  status: 'issued' | 'partially_paid' | 'paid' | 'cancelled' | 'forfeited' | 'overdue';
  hasPdf: boolean;
  pdfPending: boolean;
  payments: Array<{
    id: number;
    amount: number;
    method: string;
    gateway: string | null;
    receivedAt: string | null;
    receiptAvailable: boolean;
  }>;
  canPay: boolean;
  gateways?: InvGateway[];
  minPartial?: number;
  /** Catering pilot: present only on a TEST document (KTS-/LTS-); the pay link collects ₹10 whatever is typed. */
  isTest?: boolean;
}

const INV_GATEWAY_LABEL: Record<InvGateway, string> = { razorpay: 'Razorpay', cashfree: 'Cashfree' };
const INV_DEFAULT_MIN_PARTIAL = 1;
const INV_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `https://lamirage.co/inv/<64-hex>` → token, or null for legacy (Zoho) links. */
export function extractInvToken(paymentUrl: string | null | undefined): string | null {
  if (!paymentUrl) return null;
  const m = /\/inv\/([0-9a-f]{64})/.exec(paymentUrl);
  return m ? m[1] : null;
}

/**
 * 'YYYY-MM-DD' → '20 Sep 2026' WITHOUT constructing a Date: a bare date string is
 * parsed as UTC and renders as the previous day for any viewer west of IST.
 */
function invFmtDate(key: string | null | undefined): string {
  if (!key) return '—';
  const [y, m, d] = key.slice(0, 10).split('-');
  const mi = Number(m) - 1;
  return INV_MONTHS[mi] ? `${Number(d)} ${INV_MONTHS[mi]} ${y}` : key;
}

/** An ISO instant is unambiguous, so this one may use the (IST-pinned) locale path. */
function invFmtInstant(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const invMoney = (n: number): string =>
  Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const invWhole = (n: number): string => Math.round(Number(n ?? 0)).toLocaleString('en-IN');

/**
 * The amount rule, in one place: whole rupees; the full balance is always allowed;
 * anything less must be ≥ minPartial and ≤ balance. Mirrors the server's check so the
 * client sees the message before a request is made.
 */
function invCheckAmount(
  raw: string,
  balance: number,
  minPartial: number,
): { amount: number | null; error: string | null } {
  const full = Math.round(balance);
  const trimmed = raw.trim();
  if (!trimmed) return { amount: null, error: 'Enter the amount you want to pay.' };
  if (!/^\d+$/.test(trimmed)) return { amount: null, error: 'Enter whole rupees only (no paise).' };
  const n = Number(trimmed);
  if (n <= 0) return { amount: null, error: 'Enter the amount you want to pay.' };
  if (n > full) return { amount: null, error: `That is more than the balance of ₹${invWhole(full)}.` };
  if (n < full && n < minPartial) {
    return { amount: null, error: `Part payments must be at least ₹${invWhole(minPartial)}.` };
  }
  return { amount: n, error: null };
}

const INV_STATUS_PILL: Record<InvSummary['status'], { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'border-emerald-500/40 inv-green' },
  partially_paid: { label: 'Partly paid', className: 'border-sky-500/40 inv-sky' },
  issued: { label: 'Payment due', className: 'border-amber-500/40 inv-amber' },
  overdue: { label: 'Overdue', className: 'border-rose-500/40 inv-red' },
  cancelled: { label: 'Cancelled', className: 'border-slate-500/40 inv-muted' },
  // Booking cancelled, advance kept (owner 2026-09-28). Neutral: never 'Payment due'.
  forfeited: { label: 'Closed', className: 'border-slate-500/40 inv-muted' },
};

/* Scoped colour accents — specificity (0,2,0) beats the global `body [role="dialog"] *`
   (0,1,1) so these survive the white-text override. */
export const INV_MODAL_CSS = `
[role="dialog"] .inv-gold { color: #e0c88a !important; }
[role="dialog"] .inv-muted { color: rgba(255,255,255,0.55) !important; }
[role="dialog"] .inv-green { color: #6ee7b7 !important; }
[role="dialog"] .inv-sky { color: #7dd3fc !important; }
[role="dialog"] .inv-amber { color: #fcd34d !important; }
[role="dialog"] .inv-red { color: #fca5a5 !important; }
[role="dialog"] button.inv-chip { padding: 6px 12px !important; font-size: 12px !important; font-weight: 600 !important; }
[role="dialog"] button.inv-chip[aria-pressed="true"] { border-color: #B8860B !important; color: #e0c88a !important; }
[role="dialog"] button.inv-pay:disabled { opacity: 0.4 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }
[role="dialog"] input.inv-amount { padding: 10px 12px 10px 30px !important; font-size: 16px !important; border-radius: 8px !important; }
`;

export function InvoiceModal({ target, onClose }: { target: InvoiceModalTarget | null; onClose: () => void }) {
  const [inv, setInv] = useState<InvSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [amountStr, setAmountStr] = useState('');
  const paySectionRef = useRef<HTMLDivElement | null>(null);

  const token = target?.token ?? null;

  // Load the summary whenever a new invoice is opened; reset everything on close.
  useEffect(() => {
    setInv(null);
    setLoadError(null);
    setPayError(null);
    setPaying(false);
    setAmountStr('');
    if (!token) return;
    let cancelled = false;
    fetch(apiUrl(`/api/inv/${token}`))
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.success) throw new Error(j.error || 'Invoice not found');
        return j.data as InvSummary;
      })
      .then((d) => {
        if (cancelled) return;
        setInv(d);
        const full = Math.round(d.balance);
        setAmountStr(full > 0 ? String(full) : '');
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || 'Could not load the invoice');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Pay Now: bring the pay section into view once it exists.
  useEffect(() => {
    if (inv && target?.focusPay && inv.canPay) {
      const t = setTimeout(() => paySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      return () => clearTimeout(t);
    }
  }, [inv, target?.focusPay]);

  const minPartial = inv?.minPartial ?? INV_DEFAULT_MIN_PARTIAL;
  const amountCheck = inv ? invCheckAmount(amountStr, inv.balance, minPartial) : { amount: null, error: null };
  const fullBalance = inv ? Math.round(inv.balance) : 0;
  const halfBalance = Math.max(minPartial, Math.round(fullBalance / 2));
  const gateways: Array<InvGateway | undefined> = (inv?.gateways?.length ?? 0) > 0 ? (inv!.gateways as InvGateway[]) : [undefined];
  // Catering pilot: a TEST invoice's link collects the fixed ₹10 whatever amount is sent, so there is no amount entry —
  // the full balance is sent only because the route requires a valid amount.
  const testInv = isTestInvoice(inv);
  const payAmount: number | null = testInv ? (fullBalance > 0 ? fullBalance : null) : amountCheck.amount;
  const canSubmit = !paying && payAmount != null;

  const pay = async (gateway?: InvGateway) => {
    if (!target || paying || payAmount == null) return;
    const weddingId = target.weddingId;
    if (!weddingId) {
      setPayError('We could not identify your booking for this invoice. Please refresh and try again.');
      return;
    }
    setPaying(true);
    setPayError(null);
    try {
      const qs = new URLSearchParams({ amount: String(payAmount) });
      if (gateway) qs.set('gateway', gateway);
      const response = await makeAuthenticatedRequest(
        `/api/weddings/${weddingId}/invoices/${target.invoiceId}/payment-link?${qs.toString()}`,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.message || data.error || (response.status === 404 ? 'This invoice is already paid.' : 'Failed to get payment link'),
        );
      }
      if (!data.paymentUrl) throw new Error('No payment link is available for this invoice');
      // Same navigation rule as the original Pay Now handler: mobile navigates in place
      // (popup blockers), desktop opens a new tab and keeps the modal.
      const isMobile = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isAppBuild()) {
        // Mobile app: pay in the system browser; the app (and this modal) stay put.
        openExternal(data.paymentUrl);
        setPaying(false);
        return;
      }
      if (isMobile) {
        window.location.href = data.paymentUrl;
        return; // leave the spinner on — the page is navigating away
      }
      openExternal(data.paymentUrl, '_blank');
      setPaying(false);
    } catch (error) {
      console.error('Payment link error:', error);
      setPayError(error instanceof Error ? error.message : 'Unable to get payment link');
      setPaying(false);
    }
  };

  const pill = inv ? (INV_STATUS_PILL[inv.status] ?? INV_STATUS_PILL.issued) : null;
  /** Client name only — never bride & groom on an invoice screen (owner rule). */
  const subtitle = inv
    ? [inv.customerName, inv.weddingDate ? invFmtDate(inv.weddingDate) : null, inv.timeSlot].filter(Boolean).join(' · ')
    : '';

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl border border-[#B8860B]/30 bg-[#1f2937] text-white p-4 sm:p-6">
        <style>{INV_MODAL_CSS}</style>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 text-left">
            <span>Tax Invoice {inv?.invoiceNumber ?? ''}</span>
            {pill && (
              <span className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${pill.className}`}>{pill.label}</span>
            )}
          </DialogTitle>
          <DialogDescription className="inv-muted text-left">
            {subtitle || (inv ? '' : loadError ? '' : 'Loading invoice…')}
          </DialogDescription>
        </DialogHeader>

        {loadError && !inv && (
          <div className="rounded-lg border border-rose-500/40 p-4 text-sm">
            <p className="inv-red font-semibold">Invoice unavailable</p>
            <p className="inv-muted mt-1">{loadError}</p>
          </div>
        )}

        {!inv && !loadError && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {inv && (
          <div className="space-y-4">
            {inv.status === 'paid' && (
              <div className="rounded-lg border border-emerald-500/40 p-3 flex items-start gap-2">
                <CheckCircle className="h-5 w-5 shrink-0 mt-0.5 inv-green" />
                <p className="inv-green text-sm font-semibold">This invoice is paid in full. Thank you.</p>
              </div>
            )}
            {inv.status === 'cancelled' && (
              <div className="rounded-lg border border-white/20 p-3">
                <p className="text-sm font-semibold">This invoice has been cancelled.</p>
              </div>
            )}

            {/* Totals */}
            <div className="rounded-lg border border-white/15 p-3 sm:p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="inv-muted text-xs">Invoice total</p>
                  <p className="mt-1 text-base sm:text-lg font-semibold">₹{invMoney(inv.total)}</p>
                </div>
                <div>
                  <p className="inv-muted text-xs">Paid</p>
                  <p className="mt-1 text-base sm:text-lg font-semibold">₹{invMoney(inv.amountPaid)}</p>
                </div>
                <div>
                  <p className="inv-muted text-xs">Balance</p>
                  <p className={`mt-1 text-base sm:text-lg font-semibold ${inv.balance > 0.005 ? 'inv-gold' : ''}`}>
                    ₹{invMoney(inv.balance)}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-1.5 text-sm">
                {[
                  ['Billed to', inv.customerName],
                  // A multi-item invoice lists its rows; a single-supply one keeps the plain
                  // description. Same reasoning as ViewInvoice — the client must be able to
                  // see what the money covers, not a run-on string.
                  /**
                   * ⚠️ ' · ' and NOT '\n'. This value lands in a plain <span>, and HTML
                   * collapses a newline to a single space — so joining on '\n' rendered
                   * "Live Recording in USB — ₹750 Singari Melam — ₹10,000" with NO separator
                   * at all, strictly worse than the semicolon-separated description it
                   * replaced. ViewInvoice renders real elements and is the richer surface;
                   * this one is a label/value grid, so it needs a visible separator.
                   */
                  ['For', Array.isArray((inv as any).lineItems) && (inv as any).lineItems.length
                    ? (inv as any).lineItems.map((it: any) => `${it.description} — ₹${Number(it.amount).toLocaleString('en-IN')}`).join('  ·  ')
                    : inv.description],
                  ['Invoice date', invFmtDate(inv.invoiceDate)],
                  ['Payment due', inv.dueDate ? invFmtDate(inv.dueDate) : 'On receipt'],
                  ['Taxable value', `₹${invMoney(inv.taxable)}`],
                  [`CGST ${(inv.gstRate ?? 18) / 2}%`, `₹${invMoney(inv.cgst)}`],
                  [`SGST ${(inv.gstRate ?? 18) / 2}%`, `₹${invMoney(inv.sgst)}`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="inv-muted shrink-0">{k}</span>
                    <span className="text-right break-words">{v}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                {inv.hasPdf ? (
                  <a
                    href={assetUrl(`/api/inv/${token}/pdf`)}
                    onClick={externalLinkClick(`/api/inv/${token}/pdf`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm hover:border-[#B8860B]"
                  >
                    <Download className="h-4 w-4" /> Download PDF
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm inv-muted">
                    <Loader2 className={`h-4 w-4 ${inv.pdfPending ? 'animate-spin' : ''}`} />
                    {inv.pdfPending ? 'PDF is being prepared' : 'PDF not available yet'}
                  </span>
                )}
              </div>
            </div>

            {/* Catering pilot: the real amount stays in the totals above; only the ₹10 test payment is collected. */}
            {testInv && !inv.canPay && (
              <p className="text-amber-300 text-sm font-semibold" role="note">{TEST_BADGE_LABEL}: {TEST_INVOICE_NOTE}</p>
            )}

            {/* Pay */}
            {inv.canPay && (
              <div ref={paySectionRef} className="rounded-lg border border-[#B8860B]/40 p-3 sm:p-4">
                {testInv ? (
                  <p className="text-amber-300 text-sm font-semibold" role="note">{TEST_BADGE_LABEL}: {TEST_INVOICE_NOTE}</p>
                ) : (<>
                <p className="inv-gold font-semibold text-sm">Amount to pay</p>
                <p className="inv-muted text-xs mt-0.5">
                  {fullBalance > minPartial
                    ? minPartial > 1
                      ? `Pay the full ₹${invWhole(fullBalance)}, or any amount from ₹${invWhole(minPartial)}.`
                      : `Pay the full ₹${invWhole(fullBalance)}, or any smaller amount.`
                    : `Balance due: ₹${invWhole(fullBalance)}.`}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[10rem]">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">₹</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      aria-label="Amount to pay in rupees"
                      aria-invalid={amountCheck.error ? true : undefined}
                      value={amountStr}
                      onChange={(e) => setAmountStr(e.target.value.replace(/[^\d]/g, ''))}
                      disabled={paying}
                      className="inv-amount w-full disabled:opacity-50"
                    />
                  </div>
                  <button
                    type="button"
                    className="inv-chip rounded-lg border"
                    aria-pressed={amountCheck.amount === fullBalance}
                    disabled={paying}
                    onClick={() => setAmountStr(String(fullBalance))}
                  >
                    Full balance
                  </button>
                  {fullBalance > minPartial && halfBalance < fullBalance && (
                    <button
                      type="button"
                      className="inv-chip rounded-lg border"
                      aria-pressed={amountCheck.amount === halfBalance}
                      disabled={paying}
                      onClick={() => setAmountStr(String(halfBalance))}
                    >
                      50%
                    </button>
                  )}
                </div>

                {amountCheck.error && <p className="inv-red text-xs mt-2">{amountCheck.error}</p>}
                </>)}

                {/* One button per gateway, equal width, one row on ≥ sm. */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {gateways.map((g) => (
                    <button
                      key={g ?? 'default'}
                      type="button"
                      onClick={() => pay(g)}
                      disabled={!canSubmit}
                      className="inv-pay bg-primary inline-flex w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold"
                    >
                      {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                      {testInv
                        ? `${TEST_PAY_LABEL}${g ? ` via ${INV_GATEWAY_LABEL[g]}` : ''}`
                        : amountCheck.amount != null
                        ? `Pay ₹${invWhole(amountCheck.amount)}${g ? ` via ${INV_GATEWAY_LABEL[g]}` : ' now'}`
                        : g
                          ? `Pay via ${INV_GATEWAY_LABEL[g]}`
                          : 'Pay now'}
                    </button>
                  ))}
                </div>

                {payError && <p className="inv-red text-sm mt-3">{payError}</p>}
              </div>
            )}

            {/* Payments received */}
            {inv.payments.length > 0 && (
              <div className="rounded-lg border border-white/15 p-3 sm:p-4">
                <p className="font-semibold flex items-center gap-2 text-sm">
                  <Receipt className="h-4 w-4 inv-gold" /> Payments received
                </p>
                <ul className="mt-2 divide-y divide-white/10">
                  {inv.payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {p.amount < 0 ? '− ' : ''}₹{invMoney(Math.abs(p.amount))}
                          <span className="inv-muted font-normal"> · {p.gateway ?? p.method}</span>
                        </p>
                        <p className="inv-muted text-xs">{invFmtInstant(p.receivedAt)}</p>
                      </div>
                      {p.receiptAvailable && (
                        <a
                          href={assetUrl(`/api/inv/${token}/receipt/${p.id}`)}
                          onClick={externalLinkClick(`/api/inv/${token}/receipt/${p.id}`)}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 text-sm underline inv-gold"
                        >
                          Receipt
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
